import React, { useState } from 'react';
import type { CreateDivisionRequest, UpdateDivisionRequest } from '@sulis-liiga/common';
import {
  Category,
  DivisionStatus,
  PointSystem,
  DEFAULT_POINT_SYSTEM,
  POINT_SYSTEM_OPTIONS,
  toRoman,
} from '@sulis-liiga/common';

interface DivisionFormProps {
  initialData?: Partial<CreateDivisionRequest | UpdateDivisionRequest>;
  onSubmit: (data: CreateDivisionRequest | UpdateDivisionRequest) => void;
  onCancel: () => void;
  loading: boolean;
  isEdit?: boolean;
}

const DivisionForm: React.FC<DivisionFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading,
  isEdit = false,
}) => {
  // In edit mode initialData carries the whole ladder (name = baseName, category,
  // a tiers[] with per-tier maxTeams, plus the shared settings and status).
  const updateData = initialData as Partial<UpdateDivisionRequest> | undefined;

  // ─── Shared form state ──────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    category: (initialData as Partial<{ category: Category }>)?.category || Category.MENS_SINGLES,
    minAge: initialData?.ageRestriction?.minimum?.toString() || '',
    maxAge: initialData?.ageRestriction?.maximum?.toString() || '',
    genderRestriction: initialData?.genderRestriction || '',
    teamsToPromote: initialData?.teamsToPromote ?? 2,
    teamsToRelegate: initialData?.teamsToRelegate ?? 2,
    // New divisions default to Upcoming; edit shows the ladder's current status.
    status: updateData?.status || DivisionStatus.UPCOMING,
    pointSystem: initialData?.pointSystem || DEFAULT_POINT_SYSTEM,
  });

  // ─── Tier state (per-tier max teams). Pre-filled from the ladder when editing;
  //     the tier COUNT is fixed in edit mode (only max teams is editable). ────────
  const initialTiers = (initialData as Partial<CreateDivisionRequest>)?.tiers;
  const [tiers, setTiers] = useState<Array<{ maxTeams: number }>>(
    initialTiers && initialTiers.length > 0
      ? initialTiers.map(t => ({ maxTeams: t.maxTeams }))
      : Array.from({ length: 2 }, () => ({ maxTeams: 10 })),
  );
  const [tierCount, setTierCount] = useState(tiers.length);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ─── Tier count handler ─────────────────────────────────────────────────────
  const handleTierCountChange = (rawValue: string) => {
    const newCount = Math.max(1, parseInt(rawValue) || 1);
    setTierCount(newCount);
    setTiers(prev => {
      if (newCount > prev.length) {
        return [
          ...prev,
          ...Array.from({ length: newCount - prev.length }, () => ({ maxTeams: 10 })),
        ];
      }
      return prev.slice(0, newCount);
    });
  };

  const handleTierMaxTeamsChange = (index: number, rawValue: string) => {
    const value = Math.max(2, parseInt(rawValue) || 2);
    setTiers(prev => prev.map((t, i) => (i === index ? { ...t, maxTeams: value } : t)));
  };

  // ─── Validation ─────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Division name is required';
    }

    if (!isEdit) {
      if (!formData.category) {
        newErrors.category = 'Category is required';
      }
      if (tierCount < 1) {
        newErrors.tierCount = 'At least one tier is required';
      }
    }
    // Per-tier max teams are validated in both modes (tier count is fixed in edit).
    tiers.forEach((tier, i) => {
      if (tier.maxTeams < 2) {
        newErrors[`tier_${i}_maxTeams`] = `Tier ${i + 1} max teams must be at least 2`;
      }
    });

    if (formData.teamsToPromote < 0) {
      newErrors.teamsToPromote = 'Teams to promote cannot be negative';
    }

    if (formData.teamsToRelegate < 0) {
      newErrors.teamsToRelegate = 'Teams to relegate cannot be negative';
    }

    if (formData.minAge && formData.maxAge && Number(formData.minAge) > Number(formData.maxAge)) {
      newErrors.maxAge = 'Maximum age must be greater than minimum age';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const ageRestriction =
      formData.minAge || formData.maxAge
        ? {
            ...(formData.minAge && { minimum: Number(formData.minAge) }),
            ...(formData.maxAge && { maximum: Number(formData.maxAge) }),
          }
        : undefined;

    // Mixed Doubles always forces one male + one female; other categories use
    // the admin's explicit choice (empty = no restriction).
    const effectiveGender =
      formData.category === Category.MIXED_DOUBLES ? 'mixed' : formData.genderRestriction;

    if (isEdit) {
      // A ladder-wide edit: shared settings + status + per-tier max teams. Category
      // and tier count are fixed (not sent).
      const submitData: UpdateDivisionRequest = {
        name: formData.name,
        teamsToPromote: formData.teamsToPromote,
        teamsToRelegate: formData.teamsToRelegate,
        status: formData.status,
        pointSystem: formData.pointSystem,
        tiers: tiers.map(t => ({ maxTeams: t.maxTeams })),
        ...(ageRestriction && { ageRestriction }),
        ...(effectiveGender && { genderRestriction: effectiveGender }),
      };
      onSubmit(submitData);
    } else {
      const submitData: CreateDivisionRequest = {
        name: formData.name,
        category: formData.category,
        teamsToPromote: formData.teamsToPromote,
        teamsToRelegate: formData.teamsToRelegate,
        pointSystem: formData.pointSystem,
        status: formData.status,
        tiers: tiers.map(t => ({ maxTeams: t.maxTeams })),
        ...(ageRestriction && { ageRestriction }),
        ...(effectiveGender && { genderRestriction: effectiveGender }),
      };
      onSubmit(submitData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseInt(value)) : value,
    }));
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Division / Ladder Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Division Name
        </label>
        <input
          type="text"
          name="name"
          id="name"
          required
          value={formData.name}
          onChange={handleChange}
          className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
            errors.name ? 'border-danger-400' : 'border-gray-300'
          }`}
          placeholder={isEdit ? '' : 'e.g. MK'}
        />
        {errors.name && <p className="mt-1 text-sm text-danger-600">{errors.name}</p>}
        {!isEdit && (
          <p className="mt-1 text-xs text-gray-500">
            Used as the base name for the ladder.{' '}
            {formData.name
              ? `Tiers will be named "${formData.name} I", "${formData.name} II", etc.`
              : 'Tiers will be named "[Name] I", "[Name] II", etc.'}
          </p>
        )}
      </div>

      {/* Category (fixed when editing) + Number of tiers (create mode only) */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
            Category
          </label>
          <select
            name="category"
            id="category"
            required
            disabled={isEdit}
            value={formData.category}
            onChange={handleChange}
            className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 ${
              errors.category ? 'border-danger-400' : 'border-gray-300'
            }`}
          >
            <option value={Category.MENS_SINGLES}>Men's Singles</option>
            <option value={Category.WOMENS_SINGLES}>Women's Singles</option>
            <option value={Category.MENS_DOUBLES}>Men's Doubles</option>
            <option value={Category.WOMENS_DOUBLES}>Women's Doubles</option>
            <option value={Category.MIXED_DOUBLES}>Mixed Doubles</option>
          </select>
          {isEdit && (
            <p className="mt-1 text-xs text-gray-500">Category can't be changed after creation.</p>
          )}
          {errors.category && <p className="mt-1 text-sm text-danger-600">{errors.category}</p>}
        </div>

        {!isEdit && (
          <div>
            <label htmlFor="tierCount" className="block text-sm font-medium text-gray-700">
              Number of Tiers
            </label>
            <input
              type="number"
              id="tierCount"
              name="tierCount"
              min="1"
              value={tierCount}
              onChange={e => handleTierCountChange(e.target.value)}
              className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                errors.tierCount ? 'border-danger-400' : 'border-gray-300'
              }`}
            />
            {errors.tierCount && <p className="mt-1 text-sm text-danger-600">{errors.tierCount}</p>}
          </div>
        )}
      </div>

      {/* Age restrictions */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="minAge" className="block text-sm font-medium text-gray-700">
            Minimum Age (Optional)
          </label>
          <input
            type="number"
            name="minAge"
            id="minAge"
            min="1"
            value={formData.minAge}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="maxAge" className="block text-sm font-medium text-gray-700">
            Maximum Age (Optional)
          </label>
          <input
            type="number"
            name="maxAge"
            id="maxAge"
            min="1"
            value={formData.maxAge}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.maxAge ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.maxAge && <p className="mt-1 text-sm text-danger-600">{errors.maxAge}</p>}
        </div>
      </div>

      {/* Gender restriction. Mixed Doubles always forces one male + one female,
          so there is no gender choice for that category. */}
      {formData.category === Category.MIXED_DOUBLES ? (
        <div>
          <span className="block text-sm font-medium text-gray-700">Gender Restriction</span>
          <p className="mt-1 text-sm text-gray-500">
            Mixed Doubles: teams must be one male + one female (enforced automatically).
          </p>
        </div>
      ) : (
        <div>
          <label htmlFor="genderRestriction" className="block text-sm font-medium text-gray-700">
            Gender Restriction (Optional)
          </label>
          <select
            name="genderRestriction"
            id="genderRestriction"
            value={formData.genderRestriction}
            onChange={handleChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value="">No restriction</option>
            <option value="male">Male only</option>
            <option value="female">Female only</option>
          </select>
        </div>
      )}

      {/* Scoring system */}
      <div>
        <label htmlFor="pointSystem" className="block text-sm font-medium text-gray-700">
          Scoring system
        </label>
        <select
          name="pointSystem"
          id="pointSystem"
          value={formData.pointSystem}
          onChange={handleChange}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
        >
          {POINT_SYSTEM_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Best of 3 games.{' '}
          {PointSystem.POINTS_21 === formData.pointSystem
            ? 'Games to 21, win by 2, cap 30.'
            : 'Games to 15, win by 2, cap 21.'}
        </p>
      </div>

      {/* Promotion / relegation counts */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="teamsToPromote" className="block text-sm font-medium text-gray-700">
            Teams to Promote
            {!isEdit && (
              <span className="ml-1 font-normal text-gray-400">(interior boundaries)</span>
            )}
          </label>
          <input
            type="number"
            name="teamsToPromote"
            id="teamsToPromote"
            required
            min="0"
            value={formData.teamsToPromote}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.teamsToPromote ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {!isEdit && (
            <p className="mt-1 text-xs text-gray-500">
              Top tier promotes 0 automatically; only used at interior boundaries.
            </p>
          )}
          {errors.teamsToPromote && (
            <p className="mt-1 text-sm text-danger-600">{errors.teamsToPromote}</p>
          )}
        </div>

        <div>
          <label htmlFor="teamsToRelegate" className="block text-sm font-medium text-gray-700">
            Teams to Relegate
            {!isEdit && (
              <span className="ml-1 font-normal text-gray-400">(interior boundaries)</span>
            )}
          </label>
          <input
            type="number"
            name="teamsToRelegate"
            id="teamsToRelegate"
            required
            min="0"
            value={formData.teamsToRelegate}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.teamsToRelegate ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {!isEdit && (
            <p className="mt-1 text-xs text-gray-500">
              Bottom tier relegates 0 automatically; only used at interior boundaries.
            </p>
          )}
          {errors.teamsToRelegate && (
            <p className="mt-1 text-sm text-danger-600">{errors.teamsToRelegate}</p>
          )}
        </div>
      </div>

      {/* Tiers table — per-tier max teams (editable in both modes; tier count is
          fixed when editing). */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700">
          Tiers{' '}
          <span className="font-normal text-gray-400">— level 1 is the top (best players)</span>
        </h3>
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Tier
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Max Teams
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {tiers.map((tier, i) => (
                <tr key={i}>
                  <td className="px-4 py-2 font-medium text-gray-700">
                    {formData.name
                      ? `${formData.name} ${toRoman(i + 1)}`
                      : `Tier ${toRoman(i + 1)}`}
                    {i === 0 && (
                      <span className="ml-1 text-xs font-normal text-gray-400">(top)</span>
                    )}
                    {i === tiers.length - 1 && tiers.length > 1 && (
                      <span className="ml-1 text-xs font-normal text-gray-400">(bottom)</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      aria-label={`Max teams for tier ${i + 1}`}
                      min="2"
                      value={tier.maxTeams}
                      onChange={e => handleTierMaxTeamsChange(i, e.target.value)}
                      className={`w-24 px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        errors[`tier_${i}_maxTeams`] ? 'border-danger-400' : 'border-gray-300'
                      }`}
                    />
                    {errors[`tier_${i}_maxTeams`] && (
                      <p className="mt-1 text-xs text-danger-600">{errors[`tier_${i}_maxTeams`]}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status — the division's state (shared by all tiers). Defaults to Upcoming
          for a new division; shows the current state when editing. */}
      <div>
        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
          Status
        </label>
        <select
          name="status"
          id="status"
          value={formData.status}
          onChange={handleChange}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
        >
          <option value={DivisionStatus.UPCOMING}>Upcoming</option>
          <option value={DivisionStatus.ACTIVE}>Active</option>
          <option value={DivisionStatus.COMPLETED}>Completed</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          A division can only be set Active while its season is active.
        </p>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : isEdit ? 'Update Division' : 'Create Division'}
        </button>
      </div>
    </form>
  );
};

export default DivisionForm;
